/**
 * MODULE A FORM - OPTIONS DATA
 */
import {
	DeviceMakeModelItem,
	DqpDeviceType,
	DqpReading,
	InspectionOutcome,
	ReasonForInspection,
} from '@types';

import { mapEnumToOptions, mapStringArrayToOptions, SelectOption } from '../FormHelper';

export const affirmationOptions = [
	{ value: true, label: 'Yes' },
	{ value: false, label: 'No' },
];
export const affirmationOptionsClientsV9 = [
	{ value: true, label: 'Yes, these details are correct' },
	{ value: false, label: 'No, these details are not correct' },
];
export enum ReasonForInspectionOption {
	NewMeter = ReasonForInspection.NEW_METER,
	ExistingMeter = ReasonForInspection.REVALIDATION,
}
export const reasonForInspectionOptions = [
	{
		value: ReasonForInspectionOption.NewMeter,
		label: 'Certification of a new meter',
	},
	{
		value: ReasonForInspectionOption.ExistingMeter,
		label: 'Certification of an existing meter',
	},
];
export enum DeviceTypeOption {
	ClosedConduitMeter = 'SC Meter',
	ModularMeteringSystem = 'Modular Meter',
}
// export const deviceTypeOptions = mapEnumToOptions(DeviceTypeOption);
export const deviceTypeOptions = [
	{ value: DeviceTypeOption.ClosedConduitMeter, label: 'Closed conduit meter' },
	{
		value: DeviceTypeOption.ModularMeteringSystem,
		label: 'Modular metering system',
	},
];
export type MakeModelOption = SelectOption;
export const deviceTypeOptionMap: { [key in DeviceTypeOption]: DqpDeviceType } = {
	[DeviceTypeOption.ClosedConduitMeter]: DqpDeviceType.SC_METER,
	[DeviceTypeOption.ModularMeteringSystem]: DqpDeviceType.MODULAR_METER,
};
export const makeOther = 'Other';
export const modelOther = 'Other';
export const otherMakeOption: MakeModelOption = mapStringArrayToOptions([makeOther])[0];
export const otherModelOption: MakeModelOption = mapStringArrayToOptions([modelOther])[0];
export const makeOptionsMap = (makesModels: DeviceMakeModelItem[]): MakeModelOption[] => {
	const uniqueMakes = new Set(makesModels.filter(item => item.make).map(item => item.make));
	return Array.from(uniqueMakes).map(make => ({
		value: make as string,
		label: make as string,
	}));
};
export const makeAndModelOther = 'Sensor: Make - Other: Model - Other';
export const makeAndModelOptions = mapStringArrayToOptions([
	'Sensor: Make - A: Model - A',
	'Sensor: Make - B: Model - B',
	'Sensor: Make - C: Model - C',
	'Sensor: Make - D: Model - D',
	makeAndModelOther,
]);
export const mapMakesToOptions = (makesModels: DeviceMakeModelItem[]): MakeModelOption[] => {
	const allMakes: string[] = makesModels.map(item => item.make).filter(make => make) as string[];
	const uniqueMakes = new Set([...allMakes]);
	return mapStringArrayToOptions(Array.from(uniqueMakes));
};

export const mapModelsToOptions = (makesModels: DeviceMakeModelItem[], make: string) => {
	const allModels = makesModels
		.filter(item => item.make && item.make === make)
		.map(item => item.model)
		.filter(model => model) as string[];
	const uniqueModels = new Set([...allModels]);
	return mapStringArrayToOptions(Array.from(uniqueModels));
};
export const mapMakesModelsToOptions = (makesModels: DeviceMakeModelItem[]): MakeModelOption[] => {
	const makeAndModelNames = makesModels
		.filter(item => item.make && item.model)
		.map(item => `Sensor: Make - ${item.make}: Model - ${item.model}`);
	const makeModelOptions = mapStringArrayToOptions(makeAndModelNames);
	makeAndModelNames.push(makeAndModelOther);
	return makeModelOptions;
};
export enum SystemInstallationConfigurationOption {
	FullPipe = 'Full Pipe',
	PartiallyFilledPipe = 'Partially Filled Pipe',
	OpenChannel = 'Open Channel',
}
export const systemInstallationConfigurationOptions = [
	{ value: SystemInstallationConfigurationOption.FullPipe, label: 'Full pipe' },
	{ value: SystemInstallationConfigurationOption.PartiallyFilledPipe, label: 'Partially filled pipe' }, // prettier-ignore
	{
		value: SystemInstallationConfigurationOption.OpenChannel,
		label: 'Open channel',
	},
];
export enum WidthUnit {
	Millimeters = 'Millimeters (mm)',
	Metres = 'Metres (m)',
}
export const widthUnitOptions = mapEnumToOptions(WidthUnit);
export type DeviceReadingUnitMeasureOption = DqpReading.unitOfMeasure;
export const readingUnitMeasureOptions = [
	DqpReading.unitOfMeasure.L,
	DqpReading.unitOfMeasure.KL,
	DqpReading.unitOfMeasure.ML,
	DqpReading.unitOfMeasure.M3,
	DqpReading.unitOfMeasure.X10M3,
	DqpReading.unitOfMeasure.X100M3,
].map(item => ({ value: item, label: item }));
/* export const readingUnitMeasureOptions = unitsSymbolsAndNames.map(item => ({
	value: item.symbol,
	label: `${item.name} (${item.symbol})`,
})); */
export const coordinateOptions = mapStringArrayToOptions(['WGS84', 'GDA2020', 'GDA94']);
export enum InspectionOutcomeOption {
	Option1 = InspectionOutcome.CERTIFICATE,
	Option2 = InspectionOutcome.NOTICE_WITH_MODIFICATIONS,
	Option3 = InspectionOutcome.NOTICE,
}
export const inspectionOutcomeOptions: {
	value: string;
	label: React.ReactNode | string;
}[] = [
	{
		value: InspectionOutcomeOption.Option1 as unknown as string,
		label: (
			<>
				<strong>Compliance certificate</strong> - the water meter complies with the requirements of
				the Queensland Non-Urban Water Measurement Standards.
			</>
		),
	},
	{
		value: InspectionOutcomeOption.Option2 as unknown as string,
		label: (
			<>
				<strong>Notice with modifications</strong> - the water meter does not comply with the
				requirements of the Queensland Non-Urban Water Measurement standards but can be modified to
				comply.
			</>
		),
	},
	{
		value: InspectionOutcomeOption.Option3 as unknown as string,
		label: (
			<>
				<strong>Notice</strong> - the water meter does not comply with the requirements of the
				Queensland Non-Urban Water Measurement Standards and cannot be modified to comply.
			</>
		),
	},
];
export enum DocumentsForClientOption {
	Option1 = 'Diagnostics records (for electronic meters only)',
	Option2 = 'Configuration settings (for modular metering systems only)',
	Option3 = 'Open channel calibration report including cross-section survey (including dates), calibration method and measurements, and discharge relationship (for modular metering systems only)',
}
export const documentsForClientOptions = [
	{
		value: DocumentsForClientOption.Option1,
		label: DocumentsForClientOption.Option1,
	},
	{
		value: DocumentsForClientOption.Option2,
		label: DocumentsForClientOption.Option2,
	},
	{
		value: DocumentsForClientOption.Option3,
		label: DocumentsForClientOption.Option3,
	},
];
// TODO (uplift) this is no longer used in V9 – remove if ModuleAForm (old design, V8) is removed
export enum DocumentsForClientSummaryOption {
	Option1 = 'Diagnostics records will be shared with client within 10 business days:',
	Option2 = 'Meter configuration settings will be shared with client within 10 business days:',
	Option3 = 'Open channel calibration report will be shared with client within 10 business days:',
}
// TODO (uplift) this is no longer used in V9 – remove if ModuleAForm (old design, V8) is removed
export const documentsForClientSummaryOptions = [
	{
		value: DocumentsForClientOption.Option1,
		label: DocumentsForClientSummaryOption.Option1,
	},
	{
		value: DocumentsForClientOption.Option2,
		label: DocumentsForClientSummaryOption.Option2,
	},
	{
		value: DocumentsForClientOption.Option3,
		label: DocumentsForClientSummaryOption.Option3,
	},
];
export enum PhotoSubject {
	SerialNumber = 910000000,
	DialsOrDisplayIncludingReading = 910000001,
	OverallSite = 910000003,
	UpstreamExtent = 910000004,
	DownstreamExtent = 910000005,
	DisposedMeterFinalReading = 910000006,
	DisposedMeterSerialNumber = 910000007,
}
